// Prints one element of the page from its own clean document, so nothing around
// it (header, article, layout, preview tooling) can push it off the page or hide
// it. Copies the page's stylesheets into a hidden iframe, adds print rules of
// its own and prints that frame. Falls back to printing the whole page.
// Browser only: call it from an event handler.

const FRAME_ATTRIBUTE = 'data-print-area-frame'

// Added after the page's stylesheets so they win. Headings use the body sans
// because Fraunces distorts in browser PDFs.
const PRINT_FRAME_CSS = `
@page { margin: 14mm 12mm; }
html, body { margin: 0; background: #fff; }
body, body * { visibility: visible !important; }
[data-print-area] { position: static !important; inset: auto !important; padding: 0 !important; }
[data-print-area] .font-heading { font-family: var(--font-sans), ui-sans-serif, system-ui, sans-serif !important; letter-spacing: normal !important; }
[data-print-area] a { color: inherit !important; text-decoration: none !important; }
`

export const removePrintFrames = (): void => {
    document.querySelectorAll(`iframe[${FRAME_ATTRIBUTE}]`).forEach((node) => node.remove())
}

export const printArea = (area: HTMLElement, title: string): void => {
    removePrintFrames()

    try {
        const frame = document.createElement('iframe')
        frame.setAttribute(FRAME_ATTRIBUTE, '')
        frame.setAttribute('aria-hidden', 'true')
        frame.setAttribute('tabindex', '-1')
        frame.style.cssText = 'position:fixed;top:0;left:-10000px;width:900px;height:1200px;border:0;'
        document.body.appendChild(frame)

        const frameWindow = frame.contentWindow
        const frameDocument = frame.contentDocument
        if (!frameWindow || !frameDocument) {
            frame.remove()
            window.print()
            return
        }

        frameDocument.open()
        frameDocument.write('<!doctype html><html lang="en"><head><meta charset="utf-8"></head><body></body></html>')
        frameDocument.close()
        frameDocument.title = title

        const base = frameDocument.createElement('base')
        base.href = `${window.location.origin}/`
        frameDocument.head.appendChild(base)

        document.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
            frameDocument.head.appendChild(frameDocument.importNode(node, true))
        })
        const printStyle = frameDocument.createElement('style')
        printStyle.textContent = PRINT_FRAME_CSS
        frameDocument.head.appendChild(printStyle)
        frameDocument.documentElement.className = document.documentElement.className

        // The font variables live on the app wrapper, so carry their values across.
        const wrapper = frameDocument.createElement('div')
        wrapper.className = 'bg-background font-sans text-foreground antialiased'
        const computed = window.getComputedStyle(area)
        for (const name of ['--font-sans', '--font-heading']) {
            const value = computed.getPropertyValue(name)
            if (value) wrapper.style.setProperty(name, value)
        }
        wrapper.appendChild(frameDocument.importNode(area, true))
        frameDocument.body.appendChild(wrapper)

        const sheets = Array.from(frameDocument.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'))
        const sheetsLoaded = Promise.all(sheets.map((link) => new Promise<void>((resolve) => {
            link.addEventListener('load', () => resolve())
            link.addEventListener('error', () => resolve())
        })))
        const timeout = new Promise<void>((resolve) => window.setTimeout(resolve, 3000))

        frameWindow.addEventListener('afterprint', () => {
            window.setTimeout(() => frame.remove(), 500)
        })

        Promise.race([sheetsLoaded, timeout])
            .then(() => {
                // Lay the page out so its fonts start loading, then wait for them.
                void frameDocument.body.offsetHeight
                return Promise.race([frameDocument.fonts.ready.then(() => undefined), timeout])
            })
            .then(() => {
                frameWindow.focus()
                frameWindow.print()
            })
            .catch(() => {
                frame.remove()
                window.print()
            })
    } catch {
        window.print()
    }
}
